/* eslint no-underscore-dangle: 0 */
import React from 'react';

// material-ui
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Stack, Tooltip, Typography } from '@mui/material';

// third-party
import dayjs from 'dayjs';

// project imports
import { CrmTask } from 'types/crm';
import Chip from 'ui-component/extended/Chip';

/**
 * Shared due-date cell for CRM tasks: a formatted due datetime plus an
 * Overdue/Today chip. Extracted from the standalone CRM Tasks page so the
 * calendar Tasks panel and the Tasks page render identical labels.
 *
 * Semantics (unchanged from the original inline `dueCell`):
 *   - no due date        -> "—"
 *   - open + past due     -> "Overdue" (error chip)
 *   - open + due today    -> "Today"   (warning chip)
 *   - recurring series    -> "Recurring" chip (POGO-13345)
 *
 * This is the shared render seam for a task's due information, so the recurring
 * marker lives here to appear consistently across the standalone Tasks list and
 * the calendar Tasks panel from a single place.
 */
export const dueCell = (task: CrmTask) => {
    const recurringChip = task.isRecurring && (
        <Tooltip title="Recurring task">
            <Chip label="Recurring" size="small" chipcolor="secondary" variant="outlined" icon={<AutorenewIcon fontSize="small" />} />
        </Tooltip>
    );
    if (!task.dueAt) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">—</Typography>
                {recurringChip}
            </Stack>
        );
    }
    const due = dayjs(task.dueAt);
    const overdue = task.status === 'open' && due.isBefore(dayjs());
    const today = due.isSame(dayjs(), 'day');
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{due.format('ddd D MMM YYYY, h:mm a')}</Typography>
            {overdue && <Chip label="Overdue" size="small" chipcolor="error" />}
            {!overdue && today && task.status === 'open' && <Chip label="Today" size="small" chipcolor="warning" />}
            {recurringChip}
        </Stack>
    );
};

export default dueCell;
